import alert from '@/utils/shared/alert';
import { captureException } from '@sentry/react';
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseTransformer,
} from 'axios';
import { camelize, decamelize, decamelizeKeys } from 'humps';
import { t } from 'i18next';
import qs from 'qs';
import { createRefreshTokenInterceptor } from '../auth/interceptors/refreshTokenInterceptor';
import config from '../config';
import {
  CSRF_COOKIE_KEY,
  TRANSFORM_KEYS_PAYLOAD_IGNORE,
  TRANSFORM_KEYS_PAYLOAD_PRESERVE,
} from '../constants';
import { ResultsTypeError } from '../errors/jsonApiResponse';
import i18n from './i18n';
import { RequestObserver } from '../utils/observers/RequestObserver';
import { createRequestEvent } from '../utils/requestEvent';
import transformKeys from '../utils/transformKeys';

export const camelizeResponseKeysFn = (data: any) => {
  try {
    const jsonData = JSON.parse(data);
    return transformKeys(jsonData, {
      transformFn: camelize,
      ignoreKeys: TRANSFORM_KEYS_PAYLOAD_IGNORE.map(decamelize),
      preserveKeys: TRANSFORM_KEYS_PAYLOAD_PRESERVE,
    });
  } catch {
    return data;
  }
};

export const decamelizeRequestKeysFn = (data: any) => {
  if (data instanceof FormData) return decamelizeFormData(data);
  else
    return JSON.stringify(
      transformKeys(data, {
        transformFn: decamelize,
        ignoreKeys: TRANSFORM_KEYS_PAYLOAD_IGNORE,
        preserveKeys: TRANSFORM_KEYS_PAYLOAD_PRESERVE,
      }),
    );
};

function decamelizeFormData(formData: FormData) {
  const decamelizedFormData = new FormData();
  for (const [key, value] of formData.entries()) {
    decamelizedFormData.append(decamelize(key), value);
  }
  return decamelizedFormData;
}

export const addParamsInterceptor = (client: AxiosInstance) => {
  client.interceptors.request.use(
    function (config) {
      if (config.params?.pageSize) {
        config.params['page[size]'] = config.params.pageSize;
        delete config.params.pageSize;
      }
      if (config.params?.pageNumber) {
        config.params['page[number]'] = config.params.pageNumber;
        delete config.params.pageNumber;
      }
      config.params = decamelizeKeys(config.params);
      return config;
    },
    function (error) {
      return Promise.reject(error);
    },
  );
};

const requestObserver = new RequestObserver();

export const getRequestObserver = (): Omit<RequestObserver, 'notifyAll'> =>
  requestObserver;

export const requestObserverInterceptor = (
  response: AxiosResponse<any, any> | AxiosError,
  requestObserver: RequestObserver,
) => {
  requestObserver.notifyAll(createRequestEvent(response));
  if (response instanceof AxiosError) return Promise.reject(response);

  return response;
};

const addRequestObserversInterceptor = (client: AxiosInstance) => {
  const interceptor = (response: AxiosResponse<any, any>) =>
    requestObserverInterceptor(response, requestObserver);
  client.interceptors.response.use(interceptor, interceptor);
};

const addResultsDebugInterceptor = (client: AxiosInstance) => {
  const interceptor = (response: AxiosResponse<any, any>) => {
    if (
      response.config?.method === 'get' &&
      response.status === 200 &&
      response.data?.results
    ) {
      try {
        response.data.results.at(0);
      } catch {
        const errorDispatcher = new ResultsTypeError();
        captureException(errorDispatcher.getError(), {
          extra: {
            response: response.data,
            typeof: typeof response.data.results,
          },
        });
      }
    }
    return response;
  };
  client.interceptors.response.use(interceptor);
};

const addNetworkErrorInterceptor = (client: AxiosInstance) => {
  const errorInterceptor = (error: AxiosError) => {
    if (error.code === AxiosError.ERR_NETWORK) {
      alert.error(t('errors.networkError'));
      return new Promise(() => {});
    }
    return Promise.reject(error);
  };

  client.interceptors.response.use(undefined, errorInterceptor);
};

const requester = (
  responseTransformers: AxiosResponseTransformer[] = [],
): AxiosInstance => {
  const axiosInstance = axios.create({
    baseURL: config.BASE_URL,
    timeout: config.API_TIMEOUT_MILISECONDS,
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
      'Accept-Language': i18n.language === 'pt_BR' ? 'pt-br' : i18n.language,
    },
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: CSRF_COOKIE_KEY,
    xsrfHeaderName: 'X-CSRFToken',
    transformRequest: [decamelizeRequestKeysFn],
    transformResponse: [camelizeResponseKeysFn, ...responseTransformers],
    paramsSerializer: params =>
      qs.stringify(params, {
        encode: false,
        indices: false,
      }),
  });

  addParamsInterceptor(axiosInstance);

  createRefreshTokenInterceptor(axiosInstance);

  addRequestObserversInterceptor(axiosInstance);

  addResultsDebugInterceptor(axiosInstance);

  addNetworkErrorInterceptor(axiosInstance);

  return axiosInstance;
};

export const requesterS3 = (config?: AxiosRequestConfig): AxiosInstance =>
  axios.create({ ...config, withCredentials: false });

export default requester;
